import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';

import { bem } from 'helpers/class-name';
import { schedulesClassesPropTypes, schedulesInstructorsPropTypes, msiLastChangePropTypes } from 'constants/propTypes';
import { Card, CardSection } from 'components/Card';
import ComponentErrorBoundary from 'components/ComponentErrorBoundary';
import { DayOfWeekPicker, Input, TimePicker, InputLabel } from 'components/Form';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import StatusBox from 'components/StatusBox';
import SvgQuestion from 'components/Svgs/Question';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { BackNavLink, Title } from 'features/App';
import { useIntl } from 'react-intl';
import { getCustomDayTimes } from './EdtiScheduleForm.helpers';

import {
  SelectInstructorInput,
  SelectClassInput,
  CapacityInput,
  ScheduleTypeSelector,
  ScheduleDateInputs,
  SingleDayExceptions,
} from '../ScheduleFormInputs';
import messages from '../ScheduleForm.messages';
import ScheduleFormFooter from '../ScheduleFormFooter/ScheduleFormFooterContainer';
import { DRAFT_STATUS, CANCELLED_STATUS } from '../../Schedules.helpers';

const { block, element } = bem('schedule-form');

const getScheduleStatus = status => {
  switch (status) {
    case DRAFT_STATUS:
      return <Message message={messages.draft} />;

    case CANCELLED_STATUS:
      return <Message message={messages.cancelled} />;

    default:
      return null;
  }
};

const EditScheduleForm = ({
  classes,
  classTitle,
  handleSubmit,
  deleteSchedule,
  initialStartTime,
  instructors,
  isLoading,
  isNoEndDate,
  nextSingleDayExceptionDate,
  saveAsDraft,
  saveAsPublished,
  status,
  isRecurring,
  isPublished,
  lastChange,
}) => {
  const intl = useIntl();
  if (isLoading) {
    return (
      <Card className={block()}>
        <LoadingDots />
      </Card>
    );
  }

  const statusMessage = getScheduleStatus(status);

  return (
    <>
      <BackNavLink useHistoryHook />
      <Title>
        {classTitle}
        {statusMessage && <StatusBox className="schedule-form__status-tag">{statusMessage}</StatusBox>}
      </Title>
      <ComponentErrorBoundary>
        <Card className={block()} flush>
          <CardSection>
            <div className={element('container', 'grid')}>
              <div className="grid__item lg-1/2">
                <div className="grid">
                  <div className="grid__item">
                    <ScheduleTypeSelector disabled={isPublished} />
                  </div>
                  <div className="grid__item mt-l">
                    <ScheduleDateInputs
                      isRecurring={isRecurring}
                      isNoEndDate={isNoEndDate}
                      disabled={isRecurring && isPublished}
                    />
                  </div>
                  {isRecurring && (
                    <div className="grid__item mt-l" data-cypress="repeat-on-input">
                      <InputLabel message={messages.repeatOn} />
                      <Field name="recurrence" component={DayOfWeekPicker} />
                    </div>
                  )}
                  <div className="grid__item mt-l">
                    <div className="grid">
                      <div className="grid__item md-1/2" data-cypress="start-time-input">
                        <InputLabel message={messages.startTime} />
                        <Field
                          name="startTime"
                          component={TimePicker}
                          options={isRecurring ? getCustomDayTimes(initialStartTime, intl) : undefined}
                        />
                      </div>
                      <div className="grid__item md-1/2" data-cypress="duration-input">
                        <InputLabel message={messages.duration} />
                        <Field
                          parse={value => value && Number(value)}
                          name="durationInMinutes"
                          type="number"
                          component={Input}
                          className={element('duration-input')}
                        />
                      </div>
                    </div>
                  </div>
                  {isRecurring && (
                    <div className="grid__item mt-l">
                      <FieldArray
                        name="singleDayExceptions"
                        component={SingleDayExceptions}
                        nextSingleDayExceptionDate={nextSingleDayExceptionDate}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="grid__item lg-1/2">
                <div className="grid">
                  <div className="grid__item" data-cypress="class-input">
                    <InputLabel message={messages.class} />
                    <Field name="classId" component={SelectClassInput} classes={classes} disabled={isPublished} />
                  </div>
                  <div className="grid__item mt-l" data-cypress="instructor-input">
                    <InputLabel message={messages.instructor} optional />
                    <Field name="instructorId" component={SelectInstructorInput} instructors={instructors} />
                  </div>
                  <div className="grid__item 1/2 mt-l" data-cypress="capacity-input">
                    <div className={element('capacity-label-tooltip-flex-container')}>
                      <InputLabel className={element('capacity-label')} message={messages.capacity} optional />
                      <TooltipToggle name="tooltip-schedule-capacity">
                        <SvgQuestion className={element('tooltip-question-mark', 'ml-')} />
                      </TooltipToggle>
                      <TooltipContent name="tooltip-schedule-capacity" place="right" hoverable>
                        <Message message={messages.capacityTooltip} />
                      </TooltipContent>
                    </div>
                    <Field name="capacity" component={CapacityInput} />
                  </div>
                </div>
              </div>
            </div>
          </CardSection>
          <ScheduleFormFooter
            saveAsDraft={handleSubmit(saveAsDraft)}
            saveAsPublished={handleSubmit(saveAsPublished)}
            deleteSchedule={deleteSchedule}
            status={status}
            lastChange={lastChange}
          />
        </Card>
      </ComponentErrorBoundary>
    </>
  );
};

EditScheduleForm.propTypes = {
  classTitle: PropTypes.string,
  handleSubmit: PropTypes.func,
  saveAsDraft: PropTypes.func,
  saveAsPublished: PropTypes.func,
  isLoading: PropTypes.bool,
  classes: schedulesClassesPropTypes,
  initialStartTime: PropTypes.string,
  instructors: schedulesInstructorsPropTypes,
  isPublished: PropTypes.bool,
  isRecurring: PropTypes.bool,
  isNoEndDate: PropTypes.bool,
  deleteSchedule: PropTypes.func,
  status: PropTypes.string,
  nextSingleDayExceptionDate: PropTypes.instanceOf(Date),
  lastChange: msiLastChangePropTypes,
};

export default EditScheduleForm;
